import { LIST_ACTIONS } from 'lib/list-helper/makeListReducer';
import {
  reducer,
  initialState,
  SETTINGS_SOURCE_TYPES_ACTIONS,
} from './settingsSourceTypesListReducer';
import debounce from 'lodash.debounce';
import settingsService from './settingsService';
import { useEffect, useReducer } from 'react';
import useQueryParams from 'lib/useQueryParams';
import settingsSourceTypesFormValidator from './settingsTypesFormValidator';
import useNavigationPrompt from 'lib/useNavigationPrompt';

const fetchSettingsSourceTypes = debounce((params, dispatch) => {
  dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: true });

  return settingsService
    .getSettingsSourceTypes(params)
    .then((results) => {
      dispatch({
        type: LIST_ACTIONS.APP_LOADS_DATA,
        payload: results,
      });
      return results;
    })
    .catch((e) => {
      dispatch({
        type: LIST_ACTIONS.SHOW_ERROR,
        payload: e.message,
      });
    })
    .then(() => {
      dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: false });
    });
}, 400);

const useSettingsSourceTypesList = ({ location, history }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { setParams, getParams } = useQueryParams(location, history);

  useNavigationPrompt(state.addSourceType?.isDirty || state.editField?.isDirty);

  const userSubmitsNewSourceType = () => {
    settingsSourceTypesFormValidator(state.addSourceType, 'Source type')
      .then((data) =>
        settingsService.saveSourceType(data).then(() => {
          fetchSettingsSourceTypes(
            { page: state.page, pageSize: state.pageSize },
            dispatch,
          );
          dispatch({
            type: SETTINGS_SOURCE_TYPES_ACTIONS.USER_CANCELS_INPUT_MODAL,
          });
        }),
      )
      .catch((e) => {
        console.log(e.payload);
        dispatch({
          type: SETTINGS_SOURCE_TYPES_ACTIONS.RESET_STATE,
          payload: {
            addSourceType: e.payload,
          },
        });
      });
  };

  const editSubmitsEditSourceType = () => {
    settingsSourceTypesFormValidator(state.editField, 'Source type')
      .then((data) =>
        settingsService.saveSourceType(data).then(() => {
          fetchSettingsSourceTypes(
            { page: state.page, pageSize: state.pageSize },
            dispatch,
          );
          dispatch({
            type: SETTINGS_SOURCE_TYPES_ACTIONS.CLEAR_EDIT_SOURCE_TYPE,
          });
        }),
      )
      .catch((e) => {
        console.log(e);
        dispatch({
          type: SETTINGS_SOURCE_TYPES_ACTIONS.RESET_STATE,
          payload: {
            editField: e.payload,
          },
        });
      });
  };

  useEffect(async () => {
    dispatch({
      type: LIST_ACTIONS.APP_LOADS_DATA,
      payload: getParams(),
    });
  }, []);

  useEffect(() => {
    fetchSettingsSourceTypes(
      {
        order: {
          [state.sortBy]: state.sortOrder,
        },
        search: state.search,
        page: state.page,
        pageSize: state.pageSize,
      },
      dispatch,
    );
  }, [state.sortBy, state.sortOrder, state.search, state.page, state.pageSize]);

  const sortBy = (column, order) => {
    setParams({ sortBy: column, sortOrder: order, page: 1 });

    dispatch({
      type: LIST_ACTIONS.USER_SORTS_BY,
      payload: { column, order },
    });

    dispatch({
      type: LIST_ACTIONS.USER_SETS_PAGE,
      payload: 1,
    });
  };

  const setPage = (page) => {
    setParams({ page });

    dispatch({
      type: LIST_ACTIONS.USER_SETS_PAGE,
      payload: page,
    });
  };

  const setPageSize = (pageSize) => {
    setParams({ pageSize, page: 1 });

    dispatch({
      type: LIST_ACTIONS.USER_SETS_PAGE_SIZE,
      payload: pageSize,
    });

    dispatch({
      type: LIST_ACTIONS.USER_SETS_PAGE,
      payload: 1,
    });
  };

  const setSearch = (e) => {
    const { value } = e.target;

    dispatch({
      type: LIST_ACTIONS.USER_TYPES_IN_SEARCH_BAR,
      payload: value,
    });

    dispatch({
      type: LIST_ACTIONS.USER_SETS_PAGE,
      payload: 1,
    });

    setParams({ search: value, page: 1 });
  };

  const userSelectsEditSourceType = (label, value) => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.SET_EDIT_SOURCE_TYPE,
      payload: {
        label,
        value,
      },
    });
  };

  const userClearsEditSourceType = () => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.CLEAR_EDIT_SOURCE_TYPE,
    });
  };

  const userTypesSourceTypeName = (e) => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.USER_CHANGES_INPUT_EDIT,
      payload: e.target.value,
    });
  };

  const userTypesNewSourceTypeLabel = (e) => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.USER_CHANGES_INPUT_ADD,
      payload: e.target.value,
    });
  };

  const userOpensInputModal = () => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.USER_OPENS_INPUT_MODAL,
    });
  };
  const userCancelsInputModal = () => {
    dispatch({
      type: SETTINGS_SOURCE_TYPES_ACTIONS.USER_CANCELS_INPUT_MODAL,
    });
  };

  return {
    state,
    dispatch,
    userSelectsEditSourceType,
    userClearsEditSourceType,
    sortBy,
    setPage,
    setPageSize,
    setSearch,
    userTypesSourceTypeName,
    userOpensInputModal,
    userCancelsInputModal,
    userTypesNewSourceTypeLabel,
    userSubmitsNewSourceType,
    editSubmitsEditSourceType,
  };
};

export default useSettingsSourceTypesList;
